/**
 * デッキの文言の差し替え。
 *
 * 枚の中身は figure-decks.ts に直に書いてある。ここを直すには
 * ビルドとデプロイが要るので、誤字や言い回しを1語直したいだけでも
 * 手が出せない。そこで「上から被せる文言」だけを KV に持ち、
 * 読むときに重ねる。元の文は消さないので、被せを消せば必ず戻る。
 *
 * 直せる場所は項目名で数え上げず、「元の枚に文字列が入っている場所」と
 * 決めてある。見出しや本文だけを挙げていくと、表のセル・端末の中身・
 * 先生の一言と、直したい場所が出てくるたびに一覧を足すことになり、
 * 足し忘れた場所だけ直せないままになる。場所は
 * `rows.0.label` のような点つなぎで指す。
 *
 * 図解 (figureId で呼ぶ絵) の中の文字はここに入らない。あれは資料の
 * 座標に合わせて置いてあり、長さを変えると隣とぶつかる。
 */
import type { DeckSlide, FigureDeck } from "./figure-decks";

/**
 * 1枚ぶんの差し替え。キーは枚の中の場所、値は差し替える文。
 * 元が配列の場所 (points や choices) は配列のまま持つ
 */
export type DeckTextPatch = Record<string, string | string[]>;

/** キーは枚の番号 (0 始まり) を文字列にしたもの */
export type DeckTextOverride = Record<string, DeckTextPatch>;

/**
 * 1項目の長さの上限。枚は 1040x585 の固定版面で、これを超えると
 * どのみち画面から溢れる。上限が無いと KV に何メガでも置けてしまう
 */
const MAX_TEXT = 2000;
/** 配列の行数。箇条書きは5〜6行までという枚の決まりに合わせる */
const MAX_LINES = 12;

/**
 * 点つなぎの場所を辿って中身を取り出す。無ければ undefined。
 *
 * その場に直に置いてある項目しか見ない。継承先まで辿ると
 * `constructor.name` や `toString.length` が「元から文字列/数値がある場所」
 * として通ってしまい、枚に無い項目を生やす入口になる
 */
export function getPath(root: unknown, path: string): unknown {
  let node: unknown = root;
  for (const key of path.split(".")) {
    if (node === null || typeof node !== "object") return undefined;
    if (!Object.prototype.hasOwnProperty.call(node, key)) return undefined;
    node = (node as Record<string, unknown>)[key];
  }
  return node;
}

/** 元は書き換えず、辿った先だけ差し替えた写しを返す */
function setPath<T>(root: T, path: string, value: unknown): T {
  const [key, ...rest] = path.split(".");
  if (root === null || typeof root !== "object") return root;
  if (!Object.prototype.hasOwnProperty.call(root, key)) return root;
  const next: unknown = rest.length === 0 ? value : setPath((root as Record<string, unknown>)[key], rest.join("."), value);
  if (Array.isArray(root)) {
    const index = Number(key);
    if (!Number.isInteger(index) || index < 0 || index >= root.length) return root;
    const copy = [...root];
    copy[index] = next;
    return copy as unknown as T;
  }
  return { ...(root as Record<string, unknown>), [key]: next } as T;
}

/**
 * 1つの値を整える。
 *
 * 端末の中身やコードが入る場所があるので、頭と末尾の空白は落とさない。
 * ここで trim をかけると、字下げに意味があるコードが1行だけ左に寄ったり、
 * 末尾の改行が消えて表示がずれたりする。長さだけ抑えて、中身は預かったまま渡す
 */
function clean(value: string): string {
  return value.slice(0, MAX_TEXT);
}

/**
 * 文字列だが文言ではない項目。
 *
 * kind は枚の種類、figureId は呼ぶ絵、language は色の当て方、
 * senseiMood は顔、tone は札の色。どれも中身が文字列なので
 * 「文字列が入っている場所」で数えると素通りしてしまう。ここを
 * 打ち替えられると、文言を直したつもりで出るものが別物に変わる
 */
const STRUCTURE_KEYS = new Set([
  "kind",
  "figureId",
  // Reviewed Zukai assets resolve through a closed build-time map. An admin
  // text override may edit the slide title, but must not select another asset.
  "variant",
  "language",
  "senseiMood",
  "tone",
  // 注釈が指すコードの断片。文字列だがコードとの対応そのもので、
  // 変えると札の置き場所が消える
  "target",
]);

/**
 * その場所が文言かどうか。点つなぎの途中も見るのは、
 * `annotations.0.tone` のように末尾以外が入れ子になる場所があるため
 */
function isTextPath(path: string): boolean {
  return path.split(".").every((key) => !STRUCTURE_KEYS.has(key));
}

/**
 * 受け取った差し替えから、元の枚に無い場所・型違い・空・長すぎるものを落とす。
 *
 * 保存のときだけでなく読むときにも通す。KV には手で入れた値や
 * 昔の形の値が残り得るので、入口だけ見ていると画面が落ちる。
 *
 * 元の枚と突き合わせるのが要。「元から文字列が入っていた場所を
 * 別の文字列にする」以外は通さないので、枚の形は絶対に変わらない
 */
export function sanitizePatch(input: unknown, slide: DeckSlide): DeckTextPatch {
  const source = (input ?? {}) as Record<string, unknown>;
  const patch: DeckTextPatch = {};
  if (!source || typeof source !== "object" || Array.isArray(source)) return patch;

  for (const [path, value] of Object.entries(source)) {
    if (!isTextPath(path)) continue;
    const original = getPath(slide, path);

    if (Array.isArray(original)) {
      // 元が配列なら、中身が全部文字列のときだけ差し替えを受ける
      if (!original.every((v) => typeof v === "string")) continue;
      if (!Array.isArray(value)) continue;
      // 行数は元と同じでなければ通さない。減らせると、4択が3択に
      // なったり表の列が1つ消えたりと、文言ではなく枚の作りが変わる。
      // 行を足し引きしたいときは figure-decks.ts の側を直す
      if (value.length !== original.length) continue;
      if (original.length > MAX_LINES) continue;
      const lines = value.map((v) => (typeof v === "string" ? clean(v) : ""));
      // 1行でも空なら丸ごと落とす。空の行が混じった箇条書きは、
      // 行が消えたのか打ちかけなのかが読む側から分からない
      if (lines.some((v) => v.trim().length === 0)) continue;
      patch[path] = lines;
      continue;
    }

    if (typeof original !== "string") continue;
    if (typeof value !== "string") continue;
    const next = clean(value);
    // 空白だけの差し替えは受けない。見出しが消えて枚が読めなくなる。
    // 中身は詰めずに預かるので、コードの字下げはそのまま残る
    if (next.trim().length > 0) patch[path] = next;
  }
  return patch;
}

/** 元の枚に差し替えを重ねる。元は書き換えない */
export function applyDeckText(deck: FigureDeck, override?: DeckTextOverride): FigureDeck {
  if (!override || Object.keys(override).length === 0) return deck;
  return {
    ...deck,
    slides: deck.slides.map((slide, i) => applySlideText(slide, override[String(i)])),
  };
}

/** 枚1つぶんの重ね合わせ */
export function applySlideText(slide: DeckSlide, patch?: DeckTextPatch): DeckSlide {
  if (!patch || Object.keys(patch).length === 0) return slide;
  // Zukai text is part of the reviewed SVG geometry. Runtime text replacement
  // would either be invisible or drift from the accessible SVG title/desc.
  if (slide.kind === "zukai") return slide;
  let next = slide;
  for (const [path, value] of Object.entries(sanitizePatch(patch, slide))) {
    next = setPath(next, path, value);
  }
  return next;
}
